import React, { useState } from "react";
import { connect } from "react-redux";
import PrecertificacionActions from "../../../stores/precertificacion/Actions";
import AuthActions from "../../../stores/auth/Actions";
import { useMountEffect, useUpdateEffect } from "../../../hooks/LifeCycle";
import useEmpresa from "../../../hooks/useEmpresa";
import CustomLayout from "../../../components/CustomLayout/CustomLayout";
import CheckRender from "../../../components/Common/CheckRender";
import TittleLine from "../../../components/Common/TittleLine";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import DetallePrecertificacion from "../components/DetallePrecertificacion";
import BusquedaPrecertificacionForm from "../components/BusquedaPrecertificacionForm";
import {
  PRECERTIFICACION_WARNING,
  ESTADO_PRECERTIFICACION,
  getFooterPosition,
  getFooterWidth,
} from "../utils/PrecertificacionConstants";
import { isObjectEmpty } from "../../../utils/ObjectUtil";
import CustomAlert from "../../../components/Common/CustomAlert";
import { mostrarErrorGenerico } from "../../../utils/AlertUtils";
import { isEmptyValue } from "../../../utils/StringUtil";

const ConfirmarPrecertificacionScreen = (props) => {
  const empresa = useEmpresa();

  const [formData, setFormData] = useState({
    numeroPrecertificacion: "",
  });

  const [showDetail, setShowDetail] = useState(false);

  const [showAutorizacion, setShowAutorizacion] = useState(false);

  const [dataPrecertificacion, setDataPrecertificacion] = useState(null);

  const [dataAutorizacion, setDataAutorizacion] = useState(null);

  const [processing, setProcessing] = useState(false);

  const [showAlert, setShowAlert] = useState(false);

  const [alertConfig, setAlertConfig] = useState({});

  useMountEffect(() => {
    props.clearSesion();
  });

  /**
   * Consultar precertificación Success.
   */
  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.getPrecertificacionData) && processing) {
      setProcessing(false);

      setDataPrecertificacion(props?.getPrecertificacionData?.precertificacion);

      setShowDetail(true);
    }
  }, [props.getPrecertificacionData]);

  /**
   * Consultar precertificación Error.
   */
  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.getPrecertificacionError) && processing) {
      setProcessing(false);
      mostrarErrorGenerico(
        props?.getPrecertificacionError,
        setProcessing,
        setAlertConfig,
        setShowAlert
      );
    }
  }, [props.getPrecertificacionError]);

  /**
   * Confirmar precertificación Success.
   */
  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.confirmarPrecertificacionData) && processing) {
      setProcessing(false);

      setDataAutorizacion(props?.confirmarPrecertificacionData?.autorizacion);

      setShowAutorizacion(true);

      setDataPrecertificacion({
        ...dataPrecertificacion,
        nombreEstado: ESTADO_PRECERTIFICACION.CONVERTIDA,
      });

      const { autorizacion } = props?.confirmarPrecertificacionData;

      setAlertConfig({
        type: "success",
        title: `Precertificación ${dataPrecertificacion?.numeroPrecertificacion} confirmada con éxito!`,
        message: `Número de Autorización: (${autorizacion?.numeroAutorizacion})`,
        onConfirm: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
    }
  }, [props.confirmarPrecertificacionData]);

  /**
   * Confirmar precertificación Error.
   */
  useUpdateEffect(() => {
    if (!isObjectEmpty(props?.confirmarPrecertificacionError) && processing) {
      setProcessing(false);
      mostrarErrorGenerico(
        props?.confirmarPrecertificacionError,
        setProcessing,
        setAlertConfig,
        setShowAlert
      );
    }
  }, [props.confirmarPrecertificacionError]);

  const onStateChange =
    (key, format = null) =>
    (value) => {
      setFormData((prevState) => {
        return {
          ...prevState,
          [key]: format ? format(value) : value,
        };
      });
    };

  const busquedaPrecertificacion = () => {
    const { numeroPrecertificacion } = formData;

    if (isEmptyValue(numeroPrecertificacion)) {
      setAlertConfig({
        type: "info",
        title: "¡Hola!",
        message: "Favor ingresar el número de precertificación.",
        onConfirm: () => {
          setShowAlert(false);
        },
      });

      setShowAlert(true);
      return;
    }

    setShowAutorizacion(false);

    setShowDetail(false);

    const { numeroSesionReutilizable } = props?.authUserData;

    setProcessing(true);

    props.getPrecertificacion({
      numeroSesion: numeroSesionReutilizable,
      numeroPrecertificacion: formData.numeroPrecertificacion,
      compania: empresa?.CODIGO,
    });
  };

  const confirmar = () => {
    const { numeroSesionReutilizable } = props?.authUserData;

    setProcessing(true);

    props.confirmarPrecertificacion({
      numeroSesion: numeroSesionReutilizable,
      numeroPrecertificacion: dataPrecertificacion?.numeroPrecertificacion,
      compania: empresa?.CODIGO,
      numeroPlastico: dataPrecertificacion?.afiliado?.numeroPlastico
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      busquedaPrecertificacion();
    }
  };

  return (
    <CustomLayout
      showMenuNav={true}
      footerPosition={getFooterPosition(showDetail)}
      footerWidth={getFooterWidth(showDetail)}
    >
      <Section>
        <TittleLine title={`Confirmar Precertificación`} weight={"bold"} />
      </Section>

      <Section>
        <BusquedaPrecertificacionForm
          {...formData}
          onStateChange={onStateChange}
          onSearch={busquedaPrecertificacion}
          isLoading={props.getPrecertificacionLoading}
          handleKeyPress={handleKeyPress}
        />
      </Section>

      <CheckRender allowed={showDetail}>
        <Section>
          <DetallePrecertificacion
            dataPrecertificacion={dataPrecertificacion}
            dataAutorizacion={dataAutorizacion}
            showAutorizacion={showAutorizacion}
            actionButtonLoading={props.confirmarPrecertificacionLoading}
            onSubmitAction={confirmar}
            actionButton={"Autorizar"}
            warningText={PRECERTIFICACION_WARNING.CONFIRMAR}
          />
        </Section>
      </CheckRender>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>
    </CustomLayout>
  );
};

const mapStateToProps = (state) => ({
  authUserData: state.auth.authUserData,
  authUserLoading: state.auth.authUserLoading,
  authUserError: state.auth.authUserError,

  getPrecertificacionData: state.precertificacion.getPrecertificacionData,
  getPrecertificacionLoading: state.precertificacion.getPrecertificacionLoading,
  getPrecertificacionError: state.precertificacion.getPrecertificacionError,

  confirmarPrecertificacionData:
    state.precertificacion.confirmarPrecertificacionData,
  confirmarPrecertificacionLoading:
    state.precertificacion.confirmarPrecertificacionLoading,
  confirmarPrecertificacionError:
    state.precertificacion.confirmarPrecertificacionError,
});

const mapDispatchToProps = {
  clearSesion: AuthActions.clearSesion,
  getPrecertificacion: PrecertificacionActions.getPrecertificacion,
  confirmarPrecertificacion: PrecertificacionActions.confirmarPrecertificacion,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(ConfirmarPrecertificacionScreen);
